import { type TextProps, styled } from 'tamagui';
import { Text } from '@/components/text';

// The five-variant bg/text palette (badgeNeutralBg/Text through
// badgeErrorBg/Text) already existed in tokens.generated.ts and as real
// theme keys in tamagui.config.ts before this component did -- built
// for lib/avatar-identity.ts's decorative, non-status avatar-color
// rotation (see that file's own comment: "nothing about this circle
// conveys app state"). This component is the FIRST real consumer that
// uses the same five pairs as an actual status signal, so the variant
// names below are deliberately the semantic ones ("success"/"warning"/
// "error"/"primary"/"neutral"), not the avatar file's "which bucket did
// the hash land in" framing -- same underlying tokens, different
// meaning at the call site, which is exactly what a semantic token
// layer is for (tokens-and-color.md's 3-tier hierarchy).
//
// Non-interactive on purpose -- this is a status label, never a button
// or link, so unlike Card/PrimaryButton it does NOT get hover/press/
// focus/disabled pseudo-states (Card's own precedent: "don't invent
// states a primitive has no use for"). The one real accessibility
// requirement to get right for a badge that's read-only is
// "no color-only" (tokens-and-color.md, components.md): every
// variant here is always given actual status words as children
// ("Connected", "Declined", "Requested you" -- see
// reveal-request-status.ts's now-variant-bearing label), never just a
// colored dot with no text, so the meaning survives for a screen
// reader and for a color-blind viewer without needing a second signal.
//
// borderRadius "$full" (radius.full = 9999 in tokens.generated.ts) is
// the one radius step in that scale actually meant for a pill/chip
// shape rather than a panel corner -- every other radius token here
// ($sm/$md/$lg/$xl, used by Card/Input/PrimaryButton) is a rounded-
// rectangle corner radius, and using one of those on a short two-word
// label would read as a small rounded box, not a pill.
// paddingHorizontal "$3"/paddingVertical "$1" (12px/4px, both real
// steps on the 4px base-unit scale) keep the badge visually compact --
// this is a small inline status marker sitting next to or under other
// content (ConnectionRow's "Connected" text, ProfileRevealStatus's
// card), not a tap target in its own right, so it doesn't need
// PrimaryButton's 44pt minHeight treatment.
// fontSize "$2" and fontWeight "600" match this app's existing
// convention for small secondary/status text (e.g. MatchCard's
// "Matched {relativeTime}" line, reveal-requests's cooldown caption) --
// a badge label is exactly that register, not body text.
// alignSelf "flex-start" is set explicitly (same reasoning as
// PrimaryButton's own alignSelf/Card's explicit sizing): a Text
// rendered as a direct child of a YStack stretches to the parent's
// cross-axis width by default (RN/Tamagui's flex align-items:stretch
// default), which would turn a two-word pill into a full-width bar.
// This guarantees the pill always shrinks to its own content
// regardless of what stack it's dropped into.
const StyledBadge = styled(Text, {
  name: 'Badge',
  alignSelf: 'flex-start',
  borderRadius: '$sm',
  paddingHorizontal: '$2',
  paddingVertical: '$1',
  overflow: 'hidden',
  fontSize: '$caption',
  lineHeight: '$caption',
  fontWeight: '600',

  variants: {
    variant: {
      neutral: {
        backgroundColor: '$badgeNeutralBg',
        color: '$badgeNeutralText',
      },
      primary: {
        backgroundColor: '$badgePrimaryBg',
        color: '$badgePrimaryText',
      },
      success: {
        backgroundColor: '$badgeSuccessBg',
        color: '$badgeSuccessText',
      },
      warning: {
        backgroundColor: '$badgeWarningBg',
        color: '$badgeWarningText',
      },
      error: {
        backgroundColor: '$badgeErrorBg',
        color: '$badgeErrorText',
      },
    },
  } as const,

  defaultVariants: {
    variant: 'neutral',
  },
});

// Kept as a standalone exported type (not inlined into BadgeProps)
// specifically so lib/reveal-request-status.ts -- a jest-tested pure
// module that deliberately imports nothing from 'tamagui' (see
// avatar-identity.ts's own comment on why: jest can't parse tamagui's
// native ESM entry) -- can still type its own `variant` field against
// this exact union via `import type`, which TypeScript/Babel erase
// completely at build time, without ever pulling this component's
// runtime tamagui import into that file.
export type BadgeVariant = 'neutral' | 'primary' | 'success' | 'warning' | 'error';

export type BadgeProps = TextProps & { variant?: BadgeVariant };

export function Badge({ children, ...props }: BadgeProps) {
  return <StyledBadge {...props}>{children}</StyledBadge>;
}
